import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function HowItWorksSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const steps = [
    {
      number: '1',
      title: 'CHARGE IT',
      description: 'Plug into any USB port for 2 hours. Full charge lasts 2-3 weeks of regular use.',
      icon: 'ri-usb-line',
    },
    {
      number: '2',
      title: 'LOAD YOUR CIGARETTES',
      description: 'Open the case, insert your cigarettes. They stay fresh and protected all day.',
      icon: 'ri-inbox-line',
    },
    {
      number: '3',
      title: 'LIGHT & GO',
      description: 'Press the button, get instant windproof flame. Works every single time.',
      icon: 'ri-flashlight-line',
    },
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black mb-3 sm:mb-4">
            Simple as 1-2-3
          </h2>
          <p className="text-black/60 text-xs sm:text-sm md:text-base lg:text-lg max-w-2xl mx-auto">
            No complicated setup. No learning curve. Just three easy steps to never worry about lighters again.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative"
            >
              {/* Connector Line (hidden on mobile, shown on md+) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-black/10 z-0" />
              )}

              <div className="relative z-10 text-center">
                {/* Number Badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                  className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-black text-white font-serif text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6"
                >
                  {step.number}
                </motion.div>

                {/* Icon */}
                <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-3 sm:mb-4">
                  <i className={`${step.icon} text-3xl sm:text-4xl md:text-5xl text-black/80`} />
                </div>

                {/* Title */}
                <h3 className="font-bold text-sm sm:text-base md:text-lg lg:text-xl text-black mb-2 sm:mb-3 tracking-wide">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-black/60 text-xs sm:text-sm md:text-base leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-8 sm:mt-12 md:mt-16"
        >
          <a
            href="#offer"
            className="inline-block bg-black text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base md:text-lg font-medium hover:bg-black/90 transition-all cursor-pointer whitespace-nowrap active:scale-95"
          >
            Get Yours Now — $49.90
          </a>
        </motion.div>
      </div>
    </section>
  );
}
